import { translateInline } from '@/globals/translate/translateInline';
import {
	WebAPIBarGraphChartSlicerData,
	WebAPIChartCategory,
	WebAPIChartTopic,
} from '../../../../globals/webApiTypes/elements/WebAPICBS';
import { splitString } from '../../helpers/helpers';
import { getDimensionLabelAlias, getLabelAlias } from './labelAliasHelpers';

const barChartYAxisLabelLineLength = 20;
const barChartLegendLabelLineLength = 15;

export const matchBarChartDimensions = ({
	matchingDimensions,
	entryCategories,
}: {
	matchingDimensions: Record<string, Array<string>>;
	entryCategories: Record<string, string>;
}) => {
	return Object.entries(matchingDimensions).every(
		([dimensionKey, categoryKeys]) => {
			const entryCategoryKey = entryCategories[dimensionKey];

			return categoryKeys.includes(entryCategoryKey);
		},
	);
};

const getDimensionTitle = ({
	dimensionKey,
	selectedDimensions,
}: {
	dimensionKey: string;
	selectedDimensions: WebAPIBarGraphChartSlicerData['selectedDimensions'];
}) => {
	const title = selectedDimensions[dimensionKey].title;
	return title;
};

export const getBarChartDimensionInfo = ({
	dimensionKey,
	selectedDimensions,
	labelAliases,
}: { dimensionKey: string } & Pick<
	WebAPIBarGraphChartSlicerData,
	'selectedDimensions' | 'labelAliases'
>) => {
	const rawTitle =
		dimensionKey === 'DIMENSION_TOPIC'
			? translateInline({ en: 'Subject', nl: 'Onderwerp' })
			: getDimensionTitle({ dimensionKey, selectedDimensions });
	const labelAlias = getDimensionLabelAlias(labelAliases)(dimensionKey);
	const title = labelAlias || rawTitle;

	const legendLabel = splitString(title, barChartYAxisLabelLineLength).join(
		'\n',
	);

	return {
		title,
		legendLabel,
	};
};

export const prepareBarChartYAxisDimension =
	(labelAliases: WebAPIBarGraphChartSlicerData['labelAliases']) =>
	(props: WebAPIChartTopic | WebAPIChartCategory) => {
		const alias = getLabelAlias(labelAliases)(props);
		const title = alias || props.title;

		const axisLabel = splitString(title, barChartYAxisLabelLineLength).join(
			'\n',
		);

		return { ...props, title, axisLabel };
	};

export const prepareBarChartComparingDimension =
	(labelAliases: WebAPIBarGraphChartSlicerData['labelAliases']) =>
	(props: WebAPIChartTopic | WebAPIChartCategory) => {
		const alias = getLabelAlias(labelAliases)(props);
		const title = alias || props.title;

		const legendLabel = splitString(title, barChartLegendLabelLineLength).join(
			'\n',
		);

		return { ...props, title, legendLabel };
	};

export const prepareBarChartDimension =
	(labelAliases: WebAPIBarGraphChartSlicerData['labelAliases']) =>
	(props: WebAPIChartTopic | WebAPIChartCategory) => {
		const labelAlias = getLabelAlias(labelAliases)(props);
		const title = labelAlias || props.title;
		return {
			...props,
			labelAlias,
			title,
			info: {
				...props,
				dimensionType: 'dimensionKey' in props ? 'category' : 'topic',
				alias: labelAlias,
			},
		};
	};
